import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, EventEmitter, HostListener, inject, input, Input, OnDestroy, OnInit, Optional, Output, Renderer2, Self, signal, ViewChild, WritableSignal } from "@angular/core";
import { ControlValueAccessor, NgControl, Validators } from "@angular/forms";
import { debounceTime, Subject, Subscription } from "rxjs";
import { stringFormat } from "../functions/string-format.function";
import { TranslateService } from "@ngx-translate/core";
import { MatFormField } from "@angular/material/form-field";

@Directive()
export abstract class ControlBase<T> implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {

    protected translate = inject(TranslateService);

    @Input() formControlName = '';

    public value: T | null = null;

    private disabledFlag = false;

    protected isDisabled: WritableSignal<boolean> = signal(false);

    @Input() set disabled(disabled: boolean) {
        this.isDisabled.set(disabled);
    }

    @Input() labelKey: string = '';

    showError = input<boolean>(true);

    /** If picker should be required */
    protected _isRequired: WritableSignal<boolean> = signal(false);

    get isRequired(): boolean {
        return this._isRequired();
    }

    @Input()
    set isRequired(required: boolean) {
        this._isRequired.set(required);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private onTouched: any;
  
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected onChange: any;

    @Output() valueChanged = new EventEmitter<T>();

    protected valueChangedDebouncer: Subject<T> = new Subject<T>();

    protected subs = new Subscription();

    @Input() debounceTime = 1000;
    
    protected errorMessages: WritableSignal<string[]> = signal([]);

    public hasErrors = false;

    @ViewChild(MatFormField, { static: true }) field!: MatFormField;

    private renderer = inject(Renderer2);

    constructor(@Optional() @Self() public ngControl: NgControl) {
        if (ngControl) {
            ngControl.valueAccessor = this;
        }
    }

    private showErrorField(hasErrors: boolean): void {
        if (!this.field) {
            return;
        }

        // Find the div with the "text" class
        const textElement = this.field?._elementRef.nativeElement?.querySelector('.mat-mdc-text-field-wrapper');

        if (hasErrors) {
            // Add the new class to the div
            this.renderer?.addClass(textElement, 'mdc-text-field--invalid');
        } else {
            this.renderer?.removeClass(textElement, 'mdc-text-field--invalid');
        }
        this.hasErrors = hasErrors;
    }

    ngOnInit(): void {
        this.subs = this.valueChangedDebouncer
              .pipe(debounceTime(this.debounceTime))
              .subscribe((value) => {
                this.valueChanged.emit(value)
        });

        /** subscribes to language change for error messages */
        this.subs.add(this.translate.onLangChange.subscribe(() => {
            this.beforeLanguageChange();
            this.errorMessages.update(() => this.getErrorMessages());
        }));
    }

    ngAfterViewInit(): void {
        if (this.ngControl) {
            this._isRequired.set(this.ngControl?.control?.hasValidator(Validators.required));
        }
    }

    ngOnDestroy(): void {
        this.subs?.unsubscribe();
    }

    writeValue(value: T): void {
        this.value = value;
        this.setControlValue(this.value);
        this.valueChangedDebouncer.next(
            this.value
        );
        this.afterValueWrite();
        this.errorMessages.update(() => this.getErrorMessages());
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnChange(fn: any): void {
        this.value = this.getControlValue();
        this.onChange = fn;
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    
    setDisabledState?(isDisabled: boolean): void {
        this.disabledFlag = isDisabled;
        this.setControlDisabled(this.disabledFlag);
    }
    
    @HostListener('input', ['$event']) propagateValue(event?: Event): void {
        this.onChange(this.value);
    }
    
    @HostListener('focusout', ['$event']) propagateBlur(event: Event): void {
        this.onTouched(event);
        this.afterFocusOut(this.ngControl);
        this.errorMessages.update(() => this.getErrorMessages());
    }

    /**
     * Overridable method to insert logic after focus out
     */
    protected afterFocusOut(value?: unknown): void {}

    /**
     * Overridable method to insert logic after value write
     */
    protected afterValueWrite(): void {}

    /**
     * Overridable method to insert logic before language change
     */
    protected beforeLanguageChange(): void {}

    protected getErrorMessages(): string[] {
        if (!this.ngControl.control?.touched || !this.ngControl.control?.errors) {
            this.showErrorField(false);
            return [];
        }
    
        const errorMessages = [];
        for (const err in this.ngControl.control.errors) {
          const errorObject = this.ngControl.control.errors[err];
    
          // For custom validations
        //   if (typeof errorObject === 'string') {
        //     errorMessages.push(errorObject);
        //     return errorMessages;
        //   }
    
          switch (err) {
            case 'required':
                errorMessages.push(
                    this.translate.instant('requiredErrorMessage')
                );
                break;
            case 'minlength':
                errorMessages.push(
                    stringFormat(this.translate.instant('minValueLength'), [
                    errorObject.requiredLength,
                    errorObject.actualLength,
                    ])
                );
                break;
            case 'maxlength':
                errorMessages.push(
                    stringFormat(this.translate.instant('maxValueLength'), [
                    errorObject.requiredLength,
                    errorObject.actualLength,
                    ])
                );
                break;
            case 'min':
                errorMessages.push(
                    stringFormat(this.translate.instant('minValue'), [
                    errorObject.min,
                    errorObject.actual,
                    ])
                );
                break;
            case 'max':
                errorMessages.push(
                    stringFormat(this.translate.instant('maxValue'), [
                    errorObject.max,
                    errorObject.actual,
                    ])
                );
                break;
            case 'dateRange':
                errorMessages.push(
                    this.translate.instant('dateRangeErrorMessage')
                );
                break;
            case 'maxValues':
                errorMessages.push(
                    this.translate.instant('maxValuesErrorMessage')
                );
                break;
            case 'minValues':
                errorMessages.push(
                    this.translate.instant('minValuesErrorMessage')
                );
                break;
            case 'duplicate':
                errorMessages.push(
                    this.translate.instant('duplicate')
                );
                break;
            case 'higher':
                errorMessages.push(
                    stringFormat(this.translate.instant('higher'), [
                    this.translate.instant(errorObject)
                    ])
                );
                break;
            case 'lower':
                errorMessages.push(
                    stringFormat(this.translate.instant('lower'), [
                    this.translate.instant(errorObject)
                    ])
                );
                break;
            default:
                errorMessages.push(
                    this.translate.instant('generalValidationErrorMessage')
                );
          }
        }

        this.showErrorField(errorMessages.length > 0);
    
        return errorMessages;
    }

    protected mouseInside(event: Event): void {
        event.stopPropagation();
    }

    protected abstract getControlValue(): T | null;
    protected abstract setControlValue(val: T | null): void;
    protected abstract setControlDisabled(isDisable: boolean): void;
}